import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useSearchParams } from 'react-router-dom';
import Header from '../components/Header';

const HIDE_KEY = 'dc-live-no-navbar';

/**
 * Context, über den die Live-Views (Scorer, Beamer, Einzelspiel) die globale
 * DartCloud-Navbar ein-/ausblenden können (dezenter Toggle-Button in der
 * jeweiligen Seite, State lokal im Layout — keine Persistenz nötig).
 */
interface LiveNavbarContextValue {
  navbarVisible: boolean;
  setNavbarVisible: (visible: boolean) => void;
}

const LiveNavbarContext = createContext<LiveNavbarContextValue>({
  navbarVisible: true,
  setNavbarVisible: () => { /* noop außerhalb des LiveLayouts */ },
});

export const useLiveNavbar = () => useContext(LiveNavbarContext);

/**
 * Layout für die Turniersoftware-Vollbild-Views (Scorer, Beamer, Einzelspiel):
 * DartCloud-Navbar ist standardmäßig sichtbar; `?noNavbar=true` blendet sie
 * aus (z. B. fürs Board-Tablet oder den Beamer). Die Wahl wird in
 * sessionStorage gemerkt, damit sie beim Navigieren zwischen den Live-Views
 * (Übersicht → Einzelspiel) erhalten bleibt; `?noNavbar=false` schaltet
 * sie explizit wieder ein.
 * Ausnahme Scorer (/scorer): Navbar ist per Default AUSGEBLENDET (Vollbild
 * fürs Board-Gerät) und kann über den Menü-Button im Scorer eingeblendet werden.
 */
const LiveLayout: React.FC = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const param = searchParams.get('noNavbar');
  if (param === 'true') {
    try { sessionStorage.setItem(HIDE_KEY, 'true'); } catch { /* noop */ }
  } else if (param === 'false') {
    try { sessionStorage.removeItem(HIDE_KEY); } catch { /* noop */ }
  }

  let hideByParam = param === 'true';
  if (param === null) {
    try { hideByParam = sessionStorage.getItem(HIDE_KEY) === 'true'; } catch { /* noop */ }
  }

  // Routen-Default: Scorer ohne Navbar, Beamer/Einzelspiel mit Navbar.
  const isScorer = location.pathname.startsWith('/scorer');
  const defaultHidden = hideByParam || isScorer;

  const [hidden, setHidden] = useState(defaultHidden);

  // Beim Routenwechsel innerhalb des Layouts auf den Routen-Default zurücksetzen.
  useEffect(() => {
    setHidden(defaultHidden);
  }, [location.pathname, defaultHidden]);

  const contextValue = useMemo<LiveNavbarContextValue>(
    () => ({ navbarVisible: !hidden, setNavbarVisible: (visible) => setHidden(!visible) }),
    [hidden],
  );

  return (
    <LiveNavbarContext.Provider value={contextValue}>
      <div className="flex min-h-screen flex-col bg-black">
        {!hidden && <Header />}
        <main className="flex-1">
          <Outlet />
        </main>
      </div>
    </LiveNavbarContext.Provider>
  );
};

export default LiveLayout;
